import React from 'react';
import { Spinner, Card, Row, Col, Placeholder } from 'react-bootstrap';
import './LoadingStates.css';

// Base loading spinner component
export const LoadingSpinner = ({ 
  size = 'md', 
  variant = 'primary', 
  centered = false, 
  text = 'Loading...' 
}) => {
  const sizeMap = {
    sm: { width: '1rem', height: '1rem' },
    md: { width: '2rem', height: '2rem' },
    lg: { width: '3rem', height: '3rem' }
  };

  const spinnerStyle = sizeMap[size] || sizeMap.md;

  const spinner = (
    <div className="d-flex align-items-center">
      <Spinner
        animation="border"
        variant={variant}
        style={spinnerStyle}
        role="status"
        aria-hidden="true"
      />
      {text && <span className="ms-2">{text}</span>}
    </div>
  );

  return centered ? (
    <div className="d-flex justify-content-center align-items-center p-4">
      {spinner}
    </div>
  ) : spinner;
};

// Skeleton loader for dashboard statistics
export const DashboardStatsSkeleton = ({ count = 6 }) => {
  return (
    <Row>
      {[...Array(count)].map((_, index) => (
        <Col lg={4} md={6} className="mb-4" key={index}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="d-flex align-items-center">
              <Placeholder as="div" animation="glow" className="me-3">
                <Placeholder
                  className="rounded-circle"
                  style={{ width: '60px', height: '60px' }}
                />
              </Placeholder>
              <div className="flex-grow-1">
                <Placeholder as="h6" animation="glow">
                  <Placeholder xs={6} />
                </Placeholder>
                <Placeholder as="h3" animation="glow">
                  <Placeholder xs={4} />
                </Placeholder>
              </div>
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  );
};

// Skeleton loader for data table
export const DataTableSkeleton = ({ rows = 10, columns = 7 }) => {
  return (
    <div className="table-skeleton">
      <div className="table-responsive">
        <table className="table">
          <thead>
            <tr>
              {[...Array(columns)].map((_, index) => (
                <th key={index}>
                  <Placeholder as="div" animation="glow">
                    <Placeholder xs={8} />
                  </Placeholder>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...Array(rows)].map((_, rowIndex) => (
              <tr key={rowIndex}>
                {[...Array(columns)].map((_, colIndex) => (
                  <td key={colIndex}>
                    <Placeholder as="div" animation="glow">
                      <Placeholder xs={Math.floor(Math.random() * 8) + 4} />
                    </Placeholder>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Skeleton loader for search form
export const SearchFormSkeleton = () => {
  return (
    <Card className="border p-3 m-2">
      <Card.Body>
        <Placeholder as="h5" animation="glow" className="mb-4">
          <Placeholder xs={4} />
        </Placeholder>
        <Row>
          {[...Array(6)].map((_, index) => (
            <Col md={4} sm={6} className="mb-3" key={index}>
              <Placeholder as="div" animation="glow">
                <Placeholder xs={6} className="mb-2" />
                <Placeholder as="div" className="form-control" style={{ height: '38px' }}>
                  <Placeholder xs={12} />
                </Placeholder>
              </Placeholder>
            </Col>
          ))}
          <Col md={12} className="mb-3">
            <Placeholder as="div" animation="glow">
              <Placeholder xs={4} className="mb-2" />
              <Placeholder as="div" className="form-select" style={{ height: '38px' }}>
                <Placeholder xs={12} />
              </Placeholder>
            </Placeholder>
          </Col>
        </Row>
        <Row>
          <Col className="d-flex">
            <Placeholder.Button xs={2} className="me-2" />
            <Placeholder.Button xs={2} />
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

// Skeleton loader for patient cards
export const PatientCardSkeleton = ({ count = 3 }) => {
  return (
    <Row>
      {[...Array(count)].map((_, index) => (
        <Col md={4} className="mb-3" key={index}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <Placeholder as="h6" animation="glow">
                <Placeholder xs={8} />
              </Placeholder>
              <Placeholder as="p" animation="glow">
                <Placeholder xs={6} />
                <Placeholder xs={4} />
              </Placeholder>
              <Placeholder as="div" animation="glow">
                <Placeholder xs={3} />
              </Placeholder>
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  );
};

// Progressive loading indicator
export const ProgressiveLoader = ({ 
  steps = [], 
  currentStep = 0, 
  showStepNames = true 
}) => {
  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <div className="progressive-loader">
      <div className="progress mb-3" style={{ height: '8px' }}>
        <div
          className="progress-bar progress-bar-striped progress-bar-animated"
          role="progressbar"
          style={{ width: `${progress}%` }}
          aria-valuenow={progress}
          aria-valuemin="0"
          aria-valuemax="100"
        />
      </div>
      {showStepNames && steps.length > 0 && (
        <div className="step-indicators">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`step-indicator ${index <= currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
            >
              <div className="step-number">
                {index < currentStep ? '✓' : index + 1}
              </div>
              <div className="step-name">{step}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Pulse animation loader
export const PulseLoader = ({ count = 3, size = 'md' }) => {
  const sizeMap = {
    sm: '8px',
    md: '12px',
    lg: '16px'
  };

  const dotSize = sizeMap[size] || sizeMap.md;

  return (
    <div className="pulse-loader">
      {[...Array(count)].map((_, index) => (
        <div
          key={index}
          className="pulse-dot"
          style={{
            width: dotSize,
            height: dotSize,
            animationDelay: `${index * 0.2}s`
          }}
        />
      ))}
    </div>
  );
};

// Shimmer effect loader
export const ShimmerLoader = ({ 
  width = '100%', 
  height = '20px', 
  borderRadius = '4px',
  className = '' 
}) => {
  return (
    <div
      className={`shimmer-loader ${className}`}
      style={{
        width,
        height,
        borderRadius
      }}
    />
  );
};

// Loading overlay component
export const LoadingOverlay = ({ 
  show = false, 
  message = 'Loading...', 
  backdrop = true,
  children 
}) => {
  if (!show) return children;

  return (
    <div className="loading-overlay-container">
      {children}
      <div className={`loading-overlay ${backdrop ? 'with-backdrop' : ''}`}>
        <div className="loading-content">
          <LoadingSpinner size="lg" text={message} />
        </div>
      </div>
    </div>
  );
};

// Lazy loading wrapper
export const LazyLoadWrapper = ({ 
  isLoading, 
  error, 
  children, 
  fallback = <LoadingSpinner centered />,
  errorFallback = null 
}) => {
  if (error) {
    return errorFallback || (
      <div className="alert alert-danger" role="alert">
        <strong>Error:</strong> {error.message || 'Something went wrong'}
      </div>
    );
  }

  if (isLoading) {
    return fallback;
  }

  return children;
};

// Skeleton wrapper for any content
export const SkeletonWrapper = ({ 
  isLoading, 
  children, 
  skeleton,
  minHeight = 'auto' 
}) => {
  if (isLoading) {
    return (
      <div style={{ minHeight }}>
        {skeleton}
      </div>
    );
  }

  return children;
};

// Export all components
export default {
  LoadingSpinner,
  DashboardStatsSkeleton,
  DataTableSkeleton,
  SearchFormSkeleton,
  PatientCardSkeleton,
  ProgressiveLoader,
  PulseLoader,
  ShimmerLoader,
  LoadingOverlay,
  LazyLoadWrapper,
  SkeletonWrapper
}; 